import template from './templates/spinner.tpl.html';

const defaults = {
  VALUE: 0,
  STEP: 1,
  MIN: 0,
  INTERVAL: 200
};

class SpinnerController {
  static get $inject() {
    return ['$scope', '$attrs', '$interval', '$translate'];
  }

  constructor($scope, $attrs, $interval, $translate) {
    this.scope = $scope;
    this.$attrs = $attrs;
    this.$translate = $translate;
    this.$interval = $interval;
  }

  $onInit() {
    this.intervalPromise = undefined;
    const settings = {};

    if (this.placeholder) {
      this.$translate(this.placeholder)
        .then(placeholderText => this.placeholderText = placeholderText)
        .catch(placeholderText => this.placeholderText = placeholderText);
    }

    this.scope.$watch(() => {
      settings.min = this.getParsedValue(this.$attrs.min, defaults.MIN);
      settings.max = this.getParsedValue(this.$attrs.max);
      settings.step = this.getParsedValue(this.$attrs.customStep, defaults.STEP);
      return settings;
    }, (value) => {
      this.min = value.min;
      this.max = value.max;
      this.step = value.step;

      if (Number.isFinite(this.max)) {
        const maxlength = String(this.max).length;
        this.dynamicMinWidth = {
          width: `calc(${maxlength}em + 20px)`
        };
      }
    }, true);
  }

  get min() {
    return this.privateMin;
  }
  set min(val) {
    this.privateMin = val;
    this.ngModel.$validate();
  }

  get max() {
    return this.privateMax;
  }
  set max(val) {
    this.privateMax = val;
    this.ngModel.$validate();
  }

  getParsedValue(newValue, defaultValue) {
    if (Number.isFinite(newValue)) {
      return newValue;
    }

    newValue = Number.parseFloat(newValue);
    if ( Number.isNaN(newValue) && defaultValue !== undefined ) {
      newValue = defaultValue;
    }
    return newValue;
  }

  initialize(ngModel) {
    this.ngModel = ngModel;

    ngModel.$validators.ngNumeric = (modelValue, viewValue) => {
      const value = modelValue || viewValue;
      return Number.isFinite(this.getParsedValue(value));
    };

    ngModel.$validators.ngMin = (modelValue, viewValue) => {
      const value = modelValue || viewValue;
      return !this.isLessThanMin(this.getParsedValue(value));
    };

    ngModel.$validators.ngMax = (modelValue, viewValue) => {
      const value = modelValue || viewValue;
      return !this.isGreaterThanMax(this.getParsedValue(value));
    };

    ngModel.$render = () => this.inputValue = this.ngModel.$viewValue;
    ngModel.$formatters.push((value) => this.getParsedValue(value));
  }

  isLessThanMin(value) {
    if (isNaN(this.min)) {
      return false;
    }
    return value < this.min;
  }

  isGreaterThanMax(value) {
    if (isNaN(this.max)) {
      return false;
    }
    return value > this.max;
  }

  isDisabledOrReadOnly() {
    return !!this.isDisabled || !!this.isReadonly;
  }

  disableIncrementButton() {
    if (this.isDisabledOrReadOnly()) {
      return true;
    }
    // convert nan/undefined/null to 0 to check min max as if the unparsed value was 0.
    const valueToConsider = this.ngModel.$viewValue || defaults.VALUE;
    return this.isGreaterThanMax(valueToConsider + this.step);
  }

  disableDecrementButton() {
    if (this.isDisabledOrReadOnly()) {
      return true;
    }

    // convert nan/undefined/null to 0 to check min max as if the unparsed value was 0.
    const valueToConsider = this.ngModel.$viewValue || defaults.VALUE;
    return this.isLessThanMin(valueToConsider - this.step);
  }

  incrementCounter() {
    const increment = () => {
      if (this.disableIncrementButton()) {
        this.cancelInterval();
        return;
      }

      this.updateInput(+this.step, 'increment');
    };

    increment();
    this.setInterval(increment);
  }

  decrementCounter() {
    const decrement = () => {
      if (this.disableDecrementButton()) {
        this.cancelInterval();
        return;
      }

      this.updateInput(-this.step, 'decrement');
    };

    decrement();
    this.setInterval(decrement);
  }

  setInterval(callback) {
    this.cancelInterval();
    this.intervalPromise = this.$interval(callback, defaults.INTERVAL);
  }

  cancelInterval() {
    if (this.intervalPromise) {
      this.$interval.cancel(this.intervalPromise);
    }
  }

  updateInput(offset, reason) {
    const newValue = (this.ngModel.$viewValue || defaults.VALUE) + offset;
    this.ngModel.$setViewValue(newValue, reason);
    this.inputValue = newValue;
    this.setTouched();
  }

  changed() {
    const newValue = this.getParsedValue(this.inputValue);
    this.ngModel.$setViewValue(newValue, 'changed');
    this.inputValue = newValue;
    this.setTouched();
  }

  setTouched() {
    this.ngModel.$setTouched();
  }
}

export default () => {
  return {
    require: 'ngModel',
    restrict: 'E',
    template,
    controller: SpinnerController,
    controllerAs: 'spinner',
    bindToController: {
      isDisabled: '=?',
      isReadonly: '=?',
      placeholder: '@'
    },
    link(scope, element, attrs, ngModel) {
      scope.spinner.initialize(ngModel);
    },
    scope: {}
  };
};
